import { type OfflineProfile, OfflineProfileSchema } from '@jq4race/shared';

import type { DexieKvStore } from '../infrastructure/dexie-kv.js';

const OFFLINE_PROFILE_KV_KEY = 'offlineProfile';

/**
 * Reads the offline profile through the KvStore port and validates it
 * against `OfflineProfileSchema` before trusting it. A corrupt or
 * schema-mismatched stored value (e.g. from a future app version's shape)
 * returns null rather than throwing — the caller (the beforeLoad gate)
 * treats "no valid profile" and "no profile at all" identically.
 */
export async function readOfflineProfile(kv: DexieKvStore): Promise<OfflineProfile | null> {
  const raw = await kv.get(OFFLINE_PROFILE_KV_KEY);
  if (raw === undefined) return null;
  const parsed = OfflineProfileSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/**
 * Persists the offline profile (design D11). Called from
 * useActiveLocation's successful boot fetch — the first point in the app
 * where both the authenticated user AND their location list are known
 * together (see task 3.6's "written at login/boot" note in ADR-017).
 * Overwriting is intentional: it also serves as the "different user logs in
 * on this device" case, since the new user's boot fetch naturally replaces
 * the previous profile under the same fixed kv key.
 */
export async function saveOfflineProfile(kv: DexieKvStore, profile: OfflineProfile): Promise<void> {
  await kv.set(OFFLINE_PROFILE_KV_KEY, profile);
}

/**
 * Clears the offline profile (explicit logout — binding decision). Deletes
 * ONLY the `offlineProfile` kv row. MUST NEVER touch outbox/blobs — pending
 * check-ins must survive a sign-out so they can still sync once the operator
 * (or any same-org operator, design D12) logs back in.
 */
export async function clearOfflineProfile(kv: DexieKvStore): Promise<void> {
  await kv.remove(OFFLINE_PROFILE_KV_KEY);
}
